import type { AgendaItem } from "@app/types/api/me/agenda";

export const homeAgendaParam = "schedule";
export const homeAgendaOpenValue = "open";
// Links shared before the rename still open the schedule.
const homeAgendaLegacyOpenValues = ["week"];
export const homeAgendaPreviewLimit = 3;
export const homeAgendaWindowMs = 60 * 60 * 1000;
export const homePreviewCollapsedBodyClass = "max-h-24";

type QueryValue = string | string[] | undefined;
type QueryRecord = Record<string, QueryValue>;

function isHomeAgendaOpenValue(value: string): boolean {
  return (
    value === homeAgendaOpenValue || homeAgendaLegacyOpenValues.includes(value)
  );
}

export function isHomeAgendaOpen(value: QueryValue): boolean {
  if (Array.isArray(value)) {
    return value.some(isHomeAgendaOpenValue);
  }

  return value !== undefined && isHomeAgendaOpenValue(value);
}

export function withHomeAgendaQuery(
  query: QueryRecord,
  open: boolean
): QueryRecord {
  const { [homeAgendaParam]: _removed, ...rest } = query;
  if (!open) {
    return rest;
  }

  return { ...rest, [homeAgendaParam]: homeAgendaOpenValue };
}

export function withHomeAgendaSearch(search: string, open: boolean): string {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search
  );
  if (open) {
    params.set(homeAgendaParam, homeAgendaOpenValue);
  } else {
    params.delete(homeAgendaParam);
  }
  const qs = params.toString();

  return qs.length > 0 ? `?${qs}` : "";
}

export function isInHomeAgendaWindow(
  item: AgendaItem,
  now: Date,
  windowMs = homeAgendaWindowMs
): boolean {
  if (!item.startsAt) {
    return false;
  }

  return (
    Math.abs(new Date(item.startsAt).getTime() - now.getTime()) <= windowMs
  );
}

function comparePreviewItems(a: AgendaItem, b: AgendaItem): number {
  if (!a.startsAt !== !b.startsAt) {
    return a.startsAt ? 1 : -1;
  }
  if (a.startsAt && b.startsAt && a.startsAt !== b.startsAt) {
    return a.startsAt < b.startsAt ? -1 : 1;
  }

  return a.title.localeCompare(b.title);
}

export function previewHomeAgenda({
  overdue,
  todayItems,
  now = new Date(),
  limit = homeAgendaPreviewLimit,
  windowMs = homeAgendaWindowMs,
}: {
  overdue: AgendaItem[];
  todayItems: AgendaItem[];
  now?: Date;
  limit?: number;
  windowMs?: number;
}): AgendaItem[] {
  const windowItems = todayItems.filter((item) =>
    isInHomeAgendaWindow(item, now, windowMs)
  );
  const extraTasks = [
    ...overdue,
    ...todayItems.filter(
      (item) =>
        item.kind === "task" && !item.isDone && !windowItems.includes(item)
    ),
  ];
  const taskReserve = extraTasks.length > 0 ? 1 : 0;
  const events = windowItems.slice(0, Math.max(0, limit - taskReserve));
  const tasks = extraTasks.slice(0, limit - events.length);

  return [...events, ...tasks].sort(comparePreviewItems);
}

export function moveHomeAgendaDay(
  index: number,
  delta: -1 | 1,
  dayCount: number
): number {
  return Math.max(0, Math.min(index + delta, Math.max(0, dayCount - 1)));
}

export function agendaItemPlaceLabel(item: AgendaItem): string | null {
  if (item.kind === "routine") {
    return "Routine";
  }
  if (item.kind !== "event" || !item.href) {
    return null;
  }

  try {
    const host = new URL(item.href).hostname.replace(/^www\./, "");
    if (host === "meet.google.com" || host.endsWith(".meet.google.com")) {
      return "Google Meet";
    }
    if (host === "zoom.us" || host.endsWith(".zoom.us")) {
      return "Zoom";
    }
    if (host.includes("teams.microsoft.com")) {
      return "Teams";
    }

    return host;
  } catch {
    return null;
  }
}

export function hourFloor(now: Date): Date {
  const floor = new Date(now);
  floor.setMinutes(0, 0, 0);

  return floor;
}

export function addHours(date: Date, hours: number): Date {
  return new Date(date.getTime() + hours * homeAgendaWindowMs);
}

export function formatAgendaClock(date: Date): string {
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

// Agenda items carry a start instant but no end, so every block on the day
// grid gets the same visual length.
export const dayViewSlotMinutes = 45;

export interface DayViewBlock {
  item: AgendaItem;
  startMinute: number;
  lane: number;
  laneCount: number;
}

export function minuteOfDay(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

export function layoutDayView(items: AgendaItem[]): DayViewBlock[] {
  const timed = items
    .flatMap((item) =>
      item.startsAt
        ? [{ item, startMinute: minuteOfDay(new Date(item.startsAt)) }]
        : []
    )
    .sort((a, b) => a.startMinute - b.startMinute);

  const blocks: DayViewBlock[] = [];
  let cluster: DayViewBlock[] = [];
  let laneEnds: number[] = [];
  let clusterEnd = -1;
  const closeCluster = () => {
    cluster.forEach((block) => {
      block.laneCount = laneEnds.length;
    });
    cluster = [];
    laneEnds = [];
  };

  for (const { item, startMinute } of timed) {
    if (startMinute >= clusterEnd) {
      closeCluster();
    }
    const endMinute = startMinute + dayViewSlotMinutes;
    const freeLane = laneEnds.findIndex((end) => end <= startMinute);
    const lane = freeLane === -1 ? laneEnds.length : freeLane;
    laneEnds[lane] = endMinute;
    const block = { item, startMinute, lane, laneCount: 1 };
    cluster.push(block);
    blocks.push(block);
    clusterEnd = Math.max(clusterEnd, endMinute);
  }
  closeCluster();

  return blocks;
}

export type HomeAgendaTimelineRow =
  | { type: "item"; item: AgendaItem }
  | { type: "now" }
  | { type: "hour"; at: Date };

export function homeAgendaTimelineRows({
  items,
  now,
  includeNow = true,
}: {
  items: AgendaItem[];
  now: Date;
  includeNow?: boolean;
}): HomeAgendaTimelineRow[] {
  if (items.length === 0) {
    if (!includeNow) {
      return [];
    }

    const floor = hourFloor(now);
    const rows: HomeAgendaTimelineRow[] = [
      { type: "hour", at: addHours(floor, -1) },
    ];
    if (floor.getTime() < now.getTime()) {
      rows.push({ type: "hour", at: floor });
    }
    rows.push({ type: "now" });
    rows.push({ type: "hour", at: addHours(floor, 1) });
    rows.push({ type: "hour", at: addHours(floor, 2) });
    rows.push({ type: "hour", at: addHours(floor, 3) });

    return rows;
  }

  const untimed = items.filter((item) => !item.startsAt);
  const timed = items.filter((item) => item.startsAt);
  if (!includeNow) {
    return [...untimed, ...timed].map((item) => ({
      type: "item" as const,
      item,
    }));
  }

  const before = timed.filter(
    (item) => new Date(item.startsAt ?? 0).getTime() <= now.getTime()
  );
  const after = timed.filter(
    (item) => new Date(item.startsAt ?? 0).getTime() > now.getTime()
  );

  return [
    ...untimed.map((item) => ({ type: "item" as const, item })),
    ...before.map((item) => ({ type: "item" as const, item })),
    { type: "now" },
    ...after.map((item) => ({ type: "item" as const, item })),
  ];
}
